import * as React from 'react';
import type { InjectedIntlProps } from 'react-intl';
import { injectIntl } from 'react-intl';
import styled, { css } from 'styled-components';
import { isSearchEnhancementsMilestone1Enabled } from 'feature-flags';
import type { AnalyticsMetaData } from 'state/actions/search';
import { SEARCH_BAR_HEIGHT, SEARCH_INPUT_FONT_SIZE } from 'view/common/styled';
import { Container } from 'view/search-modal/container';
import { ResetSearchOnUnMount } from 'view/search-modal/reset-search-on-unmount';
import * as bp from 'view/styles/breakpoints';
import * as elevation from 'view/styles/elevation';
import * as grid from 'view/styles/grid';
import * as mixins from 'view/styles/mixins';
import * as transitions from 'view/styles/transitions';
import * as zIndex from 'view/styles/z-index';
import CrossIcon from '@atlaskit/icon/glyph/cross';
import SearchIcon from '@atlaskit/icon/glyph/search';
import Textfield from '@atlaskit/textfield';
import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
import { HtmlButton } from '@atlassian/help-center-common-component/analytics/html-button';
import { BackButton } from '@atlassian/help-center-common-component/back-button';
import { Flipped } from '@atlassian/help-center-common-component/flip-toolkit';
import { MODAL_EXPAND_TRANSITION_MS } from '../styled';
import messages from './messages';

export interface SearchBarProps {
    onRequestClose: () => void;
    onChange: (term: string, meta?: AnalyticsMetaData) => void;
    resetSearch?: () => void;
    term: string;
    className?: string;
}

export interface Props extends SearchBarProps {
    onSubmit: () => void;
    onFocus: () => void;
}

const highlightInputText = (ref: HTMLInputElement | null) => {
    if (ref) {
        // We want to delay the focus to the next tick so react-best-modal picks up the
        // previously focused element correctly.
        setTimeout(() => ref.select(), 0);
    }
};

const INPUT_ID = 'px-search-query';

interface SearchFieldProps {
    term: string;
    placeholder: string;
    elemBeforeInput: JSX.Element;
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
    onFocus: () => void;
}

const SearchField = (props: SearchFieldProps) => {
    const { term, placeholder, elemBeforeInput, onChange, onFocus } = props;

    return (
        <>
            {isSearchEnhancementsMilestone1Enabled() ? (
                <SearchTextField
                    id={INPUT_ID}
                    value={term}
                    onChange={onChange}
                    type="text"
                    name="term"
                    autoComplete="off"
                    spellCheck={false}
                    placeholder={placeholder}
                    required
                    onFocus={onFocus}
                    // Disabling existing violations, should be fixed when revisited.
                    // eslint-disable-next-line jsx-a11y/no-autofocus
                    autoFocus
                    elemBeforeInput={elemBeforeInput}
                />
            ) : (
                // eslint-disable-next-line styled-components-a11y/no-autofocus
                <Input
                    id={INPUT_ID}
                    value={term}
                    onChange={onChange}
                    type="text"
                    name="term"
                    autoComplete="off"
                    spellCheck={false}
                    placeholder={placeholder}
                    required
                    innerRef={highlightInputText}
                    onFocus={onFocus}
                    // Disabling existing violations, should be fixed when revisited.
                    // eslint-disable-next-line jsx-a11y/no-autofocus
                    autoFocus
                />
            )}
        </>
    );
};

export class SearchBar extends React.PureComponent<Props & InjectedIntlProps> {
    onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        this.props.onChange(e.target.value);
    };

    onSubmit = (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        this.props.onSubmit();
    };

    render() {
        const { onFocus, onRequestClose, className, term, intl } = this.props;

        return (
            <ResetSearchOnUnMount resetSearch={this.props.resetSearch}>
                <Root className={className}>
                    <HiddenLabel htmlFor={INPUT_ID}>{intl.formatMessage(messages.input)}</HiddenLabel>

                    <Container>
                        <Flipped flipId="background" translate scale>
                            <InputBackground term={term} />
                        </Flipped>
                        <Flipped flipId="icon" translate>
                            {isSearchEnhancementsMilestone1Enabled() ? (
                                <BackButtonContainer>
                                    <BackButton onClick={onRequestClose} actionSubjectId="closeSearch" />
                                </BackButtonContainer>
                            ) : (
                                <Button
                                    onClick={onRequestClose}
                                    actionSubjectId="closeSearch"
                                    data-test-id="back-button"
                                >
                                    <CrossIcon
                                        primaryColor={token('color.icon', colors.N500)}
                                        label={intl.formatMessage(messages.close)}
                                    />
                                </Button>
                            )}
                        </Flipped>

                        {/* This Flipped element has to wrap the form element or there will be strange behaviour in safari */}
                        <Flipped flipId="search" translate>
                            <Form onSubmit={this.onSubmit}>
                                <SearchField
                                    term={term}
                                    onChange={this.onChange}
                                    placeholder={intl.formatMessage(messages.input)}
                                    onFocus={onFocus}
                                    elemBeforeInput={
                                        <Icon>
                                            <SearchIcon
                                                label="search icon"
                                                aria-hidden="true"
                                                size="small"
                                                primaryColor={token('color.icon', colors.N500)}
                                            />
                                        </Icon>
                                    }
                                />
                            </Form>
                        </Flipped>
                    </Container>
                </Root>
            </ResetSearchOnUnMount>
        );
    }
}

export default injectIntl(SearchBar);

export const GUTTER = grid.unitless * 2;

const Root = styled.div`
    ${mixins.borderBox};
    ${mixins.flexCenter};
    height: ${SEARCH_BAR_HEIGHT}px;

    justify-content: space-around;
    padding: 0 ${GUTTER}px;
    position: relative;
`;

const InputBackground = styled.div<{ term: string }>`
    &::before {
        ${elevation.e300};
        ${transitions.fadeInOutTiming(MODAL_EXPAND_TRANSITION_MS)};
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        /* This should only be shown if term is truthy. */
        opacity: 0;
        /* This pseudo element should not be interactive! Else it blocks clicks to the close button and input. */
        pointer-events: none;
    }

    ${(props) =>
        mixins.applyIf(
            !props.term,
            css`
                &::before {
                    opacity: 1;
                }
            `
        )};
    position: absolute;
    height: ${SEARCH_BAR_HEIGHT}px;
    left: 0;
    right: 0;
    top: 0;
    z-index: ${zIndex.searchBackground};
    border-bottom: 1px solid ${token('color.border', colors.N30)};
    background-color: ${token('elevation.surface.overlay', colors.N0)};
`;

const HiddenLabel = styled.label`
    ${mixins.hideVisually};
`;

const BackButtonContainer = styled.div`
    position: absolute;
    left: ${GUTTER}px;
    ${bp.onlyMobile.css`
        margin-left: ${token('space.100', '8px')};
    `};
`;

const SearchTextField = styled(Textfield)`
    padding-left: ${token('space.025', '2px')};
    height: ${token('space.400', '32px')};
`;

const Icon = styled.span`
    margin-left: ${token('space.100', '8px')};
`;

const INPUT_HEIGHT = grid.unitless * 7;
const BUTTON_HEIGHT = grid.unitless * 6;

// eslint-disable-next-line rulesdir/no-styled-export
export const Form = styled.form`
    margin: 0;
    width: 100%;
`;

// eslint-disable-next-line rulesdir/no-styled-export
export const Input = styled.input`
    ${SEARCH_INPUT_FONT_SIZE};
    ${mixins.borderRadius};
    width: 100%;
    height: ${INPUT_HEIGHT}px;
    margin: 0 auto;
    padding: 0 ${GUTTER}px;
    border: 0;
    outline: none;
    font-weight: inherit;
    box-shadow: none;
    color: ${token('color.text.subtle', colors.N800)};
    background-color: ${token('color.background.input', colors.N0)};
    position: relative;
    z-index: ${zIndex.searchBackground + 1};

    /* IE 11 fixes */
    ${mixins.hideIECross};
    ${mixins.placeholder(token('color.text.subtlest', colors.N200))};
`;

// eslint-disable-next-line rulesdir/no-styled-export
export const Button = styled(HtmlButton)`
    ${mixins.focusRing.default};
    height: ${BUTTON_HEIGHT}px;
    width: ${BUTTON_HEIGHT}px;
    cursor: pointer;
    background: transparent;
    border: none;
    padding: 0;
    position: relative;

    ${bp.fromXSmall.css`
        ${mixins.centerVertically('absolute')};
        right: ${GUTTER}px;
    `};
`;
